import { useCallback, useRef, useState } from 'react';
import type { Floor, StoredReport } from '@wrp/shared';
import * as api from '../api';
import { useDialogs } from '../components/Dialogs';
import { EmptyState } from '../components/EmptyState';
import { Icon, IconButton } from '../components/Icon';
import type { IconName } from '../components/Icon';
import { EvaluationMode } from '../live/EvaluationMode';
import { INITIAL_EVAL_STATE } from '../live/evaluation';
import type { EvalState } from '../live/evaluation';
import { MonitoringMode } from '../live/MonitoringMode';
import { entryFromReport, INITIAL_MONITOR_STATE } from '../live/monitoring';
import type { MonitorState } from '../live/monitoring';
import { parseNumber, useReportFeed } from '../live/shared';
import { ReportsMode } from '../live/ReportsMode';
import { SurveyMode } from '../live/SurveyMode';
import { useApp, useSafeAction, useStreamEvent } from '../store';

type Mode = 'survey' | 'evaluation' | 'monitoring' | 'reports';

/** `needsFloor` gates the tab: reports are not floor-scoped, so that one works with no floor. */
const MODES: Array<{ id: Mode; label: string; icon: IconName; needsFloor: boolean }> = [
  { id: 'survey', label: 'Survey', icon: 'survey', needsFloor: true },
  { id: 'evaluation', label: 'Evaluation', icon: 'evaluate', needsFloor: true },
  { id: 'monitoring', label: 'Monitoring', icon: 'radar', needsFloor: true },
  { id: 'reports', label: 'Reports', icon: 'list', needsFloor: false },
];

export function LivePage() {
  const {
    floors,
    floorsLoaded,
    selectedFloor,
    selectFloor,
    refreshFloors,
    refreshReferencePoints,
    refreshFingerprints,
    refreshAps,
    pushToast,
  } = useApp();
  const dialogs = useDialogs();
  const safe = useSafeAction();
  const reports = useReportFeed();
  const [mode, setMode] = useState<Mode>('survey');
  const fileInput = useRef<HTMLInputElement | null>(null);
  // Evaluation samples live here so they survive switching between modes.
  const [evals, setEvals] = useState<Record<string, EvalState>>({});
  const floorKey = selectedFloor?.id ?? '';
  const updateEval = useCallback(
    (update: (prev: EvalState) => EvalState) =>
      setEvals((all) => ({ ...all, [floorKey]: update(all[floorKey] ?? INITIAL_EVAL_STATE) })),
    [floorKey],
  );
  // Monitoring sessions are per floor and keep collecting while another mode is shown.
  const [monitors, setMonitors] = useState<Record<string, MonitorState>>({});
  const updateMonitor = useCallback(
    (update: (prev: MonitorState) => MonitorState) =>
      setMonitors((all) => ({ ...all, [floorKey]: update(all[floorKey] ?? INITIAL_MONITOR_STATE) })),
    [floorKey],
  );
  useStreamEvent<StoredReport>('report', (report) =>
    setMonitors((all) => {
      let next = all;
      for (const [id, monitor] of Object.entries(all)) {
        if (!monitor.running || monitor.entries[report.id]) continue;
        next = { ...next, [id]: { ...monitor, entries: { ...monitor.entries, [report.id]: entryFromReport(report) } } };
      }
      return next;
    }),
  );

  const floorForm = (title: string, floor?: Floor) =>
    dialogs.form(title, [
      { key: 'name', label: 'Name', value: floor?.name ?? '', placeholder: 'Level 1' },
      { key: 'width', label: 'Width (m)', type: 'number', step: '0.1', min: 0.1, value: String(floor?.width_m ?? 30) },
      { key: 'height', label: 'Height (m)', type: 'number', step: '0.1', min: 0.1, value: String(floor?.height_m ?? 20) },
    ]);

  const parseFloorForm = (values: Record<string, string>) => {
    const name = values.name?.trim();
    const width = parseNumber(values.width);
    const height = parseNumber(values.height);
    if (!name || width === null || height === null || width <= 0 || height <= 0) {
      pushToast('error', 'A name and a positive width / height are required');
      return null;
    }
    return { name, width_m: width, height_m: height };
  };

  const addFloor = async () => {
    const values = await floorForm('Add floor');
    const body = values && parseFloorForm(values);
    if (!body) return;
    const created = await safe('Failed to add floor', () => api.createFloor(body));
    if (created) {
      await refreshFloors();
      selectFloor(created.id);
    }
  };

  const editFloor = async () => {
    if (!selectedFloor) return;
    const values = await floorForm(`Edit ${selectedFloor.name}`, selectedFloor);
    const body = values && parseFloorForm(values);
    if (!body) return;
    const updated = await safe('Failed to update floor', () =>
      api.updateFloor(selectedFloor.id, {
        ...body,
        origin_lat: selectedFloor.origin_lat ?? null,
        origin_lon: selectedFloor.origin_lon ?? null,
        rotation_deg: selectedFloor.rotation_deg ?? null,
      }),
    );
    if (updated) await refreshFloors();
  };

  const deleteFloor = async () => {
    if (!selectedFloor) return;
    const ok = await dialogs.confirm(
      `Delete floor "${selectedFloor.name}" together with its reference points, fingerprints and ` +
        'the reports that were surveyed on it?',
    );
    if (!ok) return;
    const res = await safe('Failed to delete floor', () => api.deleteFloor(selectedFloor.id));
    await Promise.all([refreshFloors(), refreshReferencePoints(), refreshFingerprints(), refreshAps()]);
    if (res) {
      pushToast(
        'success',
        res.reports_deleted > 0
          ? `Floor deleted with ${res.reports_deleted} report${res.reports_deleted === 1 ? '' : 's'}`
          : 'Floor deleted',
      );
    }
  };

  const uploadImage = async (file: File | undefined) => {
    if (!file || !selectedFloor) return;
    const updated = await safe('Failed to upload floor plan', () => api.uploadFloorImage(selectedFloor.id, file));
    if (updated) {
      await refreshFloors();
      pushToast('success', 'Floor plan updated');
    }
  };

  return (
    <div className="live">
      <div className="live__bar">
        <Icon name="layers" />
        <select
          className="live__floor"
          value={selectedFloor?.id ?? ''}
          onChange={(e) => selectFloor(e.target.value || null)}
          aria-label="Active floor"
        >
          {floors.length === 0 ? <option value="">no floors</option> : null}
          {floors.map((f) => (
            <option key={f.id} value={f.id}>
              {f.name}
            </option>
          ))}
        </select>
        <IconButton icon="plus" label="Add floor" onClick={() => void addFloor()} />
        <IconButton icon="edit" label="Edit floor" disabled={!selectedFloor} onClick={() => void editFloor()} />
        <IconButton icon="upload" label="Upload floor plan image" disabled={!selectedFloor} onClick={() => fileInput.current?.click()} />
        <IconButton icon="trash" label="Delete floor" danger disabled={!selectedFloor} onClick={() => void deleteFloor()} />
        <input
          ref={fileInput}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/svg+xml"
          hidden
          onChange={(e) => {
            void uploadImage(e.target.files?.[0]);
            e.target.value = '';
          }}
        />

        <span className="box__spacer" />

        <div className="segmented" role="tablist" aria-label="Mode">
          {MODES.map((m) => (
            <button
              key={m.id}
              type="button"
              role="tab"
              aria-selected={mode === m.id}
              className={`segmented__item${mode === m.id ? ' is-active' : ''}`}
              disabled={m.needsFloor && !selectedFloor}
              onClick={() => setMode(m.id)}
            >
              <Icon name={m.icon} size={15} />
              {m.label}
            </button>
          ))}
        </div>
      </div>

      <div className="live__body">
        {mode === 'reports' ? (
          <ReportsMode />
        ) : !selectedFloor ? (
          floorsLoaded ? (
            <div className="live__empty">
              <EmptyState title="No floors yet">Use the + button above to add the first floor.</EmptyState>
            </div>
          ) : null
        ) : mode === 'survey' ? (
          <SurveyMode key={selectedFloor.id} floor={selectedFloor} latest={reports[0] ?? null} />
        ) : mode === 'evaluation' ? (
          <EvaluationMode
            key={selectedFloor.id}
            floor={selectedFloor}
            reports={reports}
            state={evals[selectedFloor.id] ?? INITIAL_EVAL_STATE}
            onChange={updateEval}
          />
        ) : (
          <MonitoringMode
            key={selectedFloor.id}
            floor={selectedFloor}
            state={monitors[selectedFloor.id] ?? INITIAL_MONITOR_STATE}
            onChange={updateMonitor}
          />
        )}
      </div>
    </div>
  );
}
